import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Sparkles, Star, Menu, X } from "lucide-react";
import {
  NavigationMenu,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  navigationMenuTriggerStyle,
} from "./ui/navigation-menu";
import { ThemeToggle } from "./theme-toggle";
import { cn } from "../lib/utils";

const NAV_LINKS = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/interests", label: "Interests" },
  { to: "/sillyprojects", label: "Silly Projects" },
];

const Navbar = () => {
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);

  const isActive = (path) => location.pathname === path;

  // Tidy up: close the mobile menu whenever the route changes.
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  return (
    <div className="sticky top-0 z-50 border-b border-border/60 bg-background/80 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      {/* A faint gold sheen along the bottom edge. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-primary/60 to-transparent"
      />
      <div className="container mx-auto flex h-16 items-center justify-between gap-4 px-4">
        {/* Brand mark — a friendly gold star with a little sparkle. */}
        <Link
          to="/"
          className="group flex items-center gap-2 text-lg font-bold tracking-tight"
        >
          <span className="relative flex h-8 w-8 items-center justify-center rounded-full bg-primary text-primary-foreground transition-all duration-300 group-hover:rotate-[18deg] group-hover:scale-110 group-hover:glow-gold">
            <Star className="h-4 w-4 transition-transform group-hover:scale-110" />
          </span>
          <span className="text-gold-shimmer">keyoni</span>
          <Sparkles className="h-4 w-4 -translate-x-1 text-primary opacity-0 transition-all duration-300 group-hover:translate-x-0 group-hover:opacity-100 group-hover:animate-wiggle" />
        </Link>

        <div className="flex items-center gap-2">
          {/* Desktop links — collapse into a menu on small screens. */}
          <NavigationMenu className="hidden md:flex">
            <NavigationMenuList>
              {NAV_LINKS.map(({ to, label }) => {
                const active = isActive(to);
                return (
                  <NavigationMenuItem key={to}>
                    <NavigationMenuLink asChild>
                      <Link
                        to={to}
                        className={cn(
                          navigationMenuTriggerStyle(),
                          // Gold underline that grows from the center; lit and held for the active page.
                          "relative bg-transparent transition-colors",
                          "after:absolute after:bottom-1.5 after:left-4 after:right-4 after:h-0.5 after:origin-center after:rounded-full after:bg-primary after:transition-transform after:duration-300 after:content-['']",
                          active
                            ? "font-medium text-primary after:scale-x-100"
                            : "after:scale-x-0 hover:text-primary hover:after:scale-x-100"
                        )}
                      >
                        {label}
                      </Link>
                    </NavigationMenuLink>
                  </NavigationMenuItem>
                );
              })}
            </NavigationMenuList>
          </NavigationMenu>

          <ThemeToggle />

          {/* Mobile menu toggle. */}
          <button
            type="button"
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((open) => !open)}
            className="inline-flex h-10 w-10 items-center justify-center rounded-md text-foreground transition-colors hover:bg-accent hover:text-primary md:hidden"
          >
            {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {/* Mobile dropdown panel. */}
      {menuOpen && (
        <nav className="border-t border-border/60 bg-background/95 backdrop-blur md:hidden">
          <div className="container mx-auto flex flex-col gap-1 px-4 py-3">
            {NAV_LINKS.map(({ to, label }) => {
              const active = isActive(to);
              return (
                <Link
                  key={to}
                  to={to}
                  onClick={() => setMenuOpen(false)}
                  className={cn(
                    "flex items-center gap-2 rounded-lg px-3 py-2.5 text-base transition-colors",
                    active
                      ? "bg-accent/60 font-medium text-primary"
                      : "text-foreground/80 hover:bg-accent/40 hover:text-primary"
                  )}
                >
                  <Star
                    className={cn(
                      "h-3.5 w-3.5 transition-opacity",
                      active ? "fill-primary text-primary opacity-100" : "opacity-0"
                    )}
                  />
                  {label}
                </Link>
              );
            })}
          </div>
        </nav>
      )}
    </div>
  );
};

export default Navbar;
